import { Link } from "react-router-dom";

import type { CheckResult, SmeCardView } from "../../../api/types";
import { Card } from "../../../ui/Card";
import { Pill } from "../../../ui/Pill";
import { SheetIcon } from "../../../ui/SheetIcon";
import { StateBadge, displayState } from "../../../ui/StateBadge";

const SEVERITY_WORDS: Record<CheckResult["severity"], string> = { error: "Error", warning: "Warning", note: "Note" };

/** What blocks Validate, and the checks that ran on this element. */
export function ChecksBlock({ card }: { card: SmeCardView }) {
  const { blockers, checks } = card;
  return (
    <Card className="ra-section" aria-labelledby="ra-checks-title">
      <h2 id="ra-checks-title">Checks</h2>
      {blockers.length > 0 ? (
        <div className="ra-blockers" role="group" aria-label="What blocks Validate">
          <p className="ra-blockers__title">Cannot be validated yet. You can still send it back or add a note.</p>
          <ul>
            {blockers.map((blocker, index) => (
              <li key={`${blocker.code}-${index}`}>
                {blocker.code === "HOME" && <Pill>Home first</Pill>} {blocker.message}
                {blocker.code !== "HOME" && blocker.code && <code className="check-code muted"> {blocker.code}</code>}
                {blocker.code === "HOME" && blocker.ref && (
                  <>
                    {" "}
                    <Link to={`/inbox/${encodeURIComponent(blocker.ref)}`} state={{ shown: true }}>
                      Open the home card
                    </Link>
                  </>
                )}
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="muted">Nothing blocks Validate.</p>
      )}
      {checks.length === 0 ? (
        <p className="muted">No check has anything to say about this element.</p>
      ) : (
        <ul className="ra-checks">
          {checks.map((check, index) => (
            <li key={`${check.code ?? "check"}-${index}`} data-severity={check.severity}>
              <strong>{SEVERITY_WORDS[check.severity]}</strong>
              {check.code && <code> {check.code}</code>}: {check.message}
              {check.blocks && <span className="muted"> (blocks Validate)</span>}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/** The symbol for each kind of drawing, read-only: it is validated with the item (DEC-1016-56). */
export function SymbolsBlock({ card }: { card: SmeCardView }) {
  if (card.symbols.length === 0) return null;
  return (
    <Card className="ra-section" aria-labelledby="ra-symbols-title">
      <h2 id="ra-symbols-title">Symbols</h2>
      <ul className="ra-symbols">
        {card.symbols.map((drawn) => (
          <li key={drawn.document_kind_id} className="row">
            <SheetIcon kind={drawn.deliverable_kind} label={drawn.name} size={18} />
            <strong>{drawn.name}</strong>
            {drawn.not_drawn ? (
              <span className="muted">Not drawn</span>
            ) : (
              <>
                {drawn.symbol && (
                  <span>
                    Symbol <code>{drawn.symbol.code}</code>
                  </span>
                )}
                {drawn.line_style && (
                  <span>
                    Line style <code>{drawn.line_style.code}</code>
                  </span>
                )}
                {drawn.symbol?.state && displayState(drawn.symbol.state) && <StateBadge state={displayState(drawn.symbol.state)!} />}
              </>
            )}
          </li>
        ))}
      </ul>
      <p className="muted">The symbol is validated with the element. It is shown here to look at, not to decide on its own.</p>
    </Card>
  );
}

/** How many places use the element. */
export function UsedIn({ count }: { count: number }) {
  return (
    <p className="ra-usedin">
      <strong>{count === 0 ? "Not used anywhere yet" : count === 1 ? "Used in 1 place" : `Used in ${count} places`}</strong>
      {count > 0 && <span className="muted"> A change to this element reaches all of them.</span>}
    </p>
  );
}
